import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './Dropdown.module.css';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/20/solid';
import { BaseProps } from '@/types/BaseProps';

export type Variant = 'primary' | 'secondary' | 'tertiary';
export interface DropdownProps extends BaseProps {
	data: Item[];
	selected?: string | null;
	className?: string;
	selectedClassName?: string;
    selectedNameWrapper?: string;
	itemsWrapperClassName?: string;
	itemClassName?: string;
	noBorder?: boolean;
	variant?: Variant;
	dict?: any;
	onChange?: (item: Item) => void;
	prependIcon?: React.ReactNode;
	placeholder?: string;
}

export interface Item {
	name: string;
	value: string;
}

export function Dropdown({
	data = [],
	selected = '',
	className = '',
	selectedClassName = '',
	itemsWrapperClassName = '',
	selectedNameWrapper = '',
	itemClassName = '',
	noBorder,
	variant = 'primary',
	onChange,
	'data-testid': testId,
	prependIcon,
	placeholder,
}: DropdownProps) {
	const [open, setOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const handleChange = (item: Item) => {
		setSelectedOption(item);
		onChange?.(item);
		setOpen(false);
	};

	const [selectedOption, setSelectedOption] = useState<Item>({ name: '', value: selected ?? '' });

	useEffect(() => {
		const preSelectedOption = data.find((entry) => entry.value === selectedOption?.value);

		if (preSelectedOption) {
			setSelectedOption(preSelectedOption);
		}
	}, [selectedOption, selected, data]);

	const chevronIconColor = useMemo(() => {
		if (variant === 'primary') return 'max-[1024px]:text-mercury-50 text-white hover:max-[1024px]:text-mercury-80';
		else if (variant === 'secondary') return 'text-mercury-50';
		else if (variant === 'tertiary') return 'text-white';
		return '';
	}, [variant]);

	return (
		<div
			className={`
			${styles.dropdown}
			${open ? styles.dropdownOpen : ''}
			${data.length > 5 ? styles.withScroll : ''}
			${styles[variant]}
			${className}`}
			ref={dropdownRef}
			data-testid={testId}
		>
			<div
				data-testid={testId ? `${testId}-toggle` : null}
				className={`${styles.selected} ${className} ${open ? styles.changeSelectedText : ''} ${noBorder ? styles.noBorder : ''
					} ${selectedClassName}`}
				onClick={() => data.length > 0 && setOpen(!open)}
			>
				<div className={`flex items-center ${styles.selectedNameWrapper} ${selectedNameWrapper}`}>
					{prependIcon && <div className={styles.prependIcon}>{prependIcon}</div>}

					<span className={styles.itemName} data-testid={testId ? `${testId}-selected-option` : null}>
						{(variant === 'secondary' || variant === 'tertiary') && open && placeholder ? (
							<>{placeholder}</>
						) : (
							<>{selectedOption.name}</>
						)}
					</span>
				</div>
				{data.length > 0 && (
					<ChevronDownIcon className={`${styles.chevronIcon} ${chevronIconColor} h-7 w-7 ${open ? styles.open : styles.closed}`} />
				)}
			</div>

			<div className={`${styles.items} ${itemsWrapperClassName}`}>
				{data.map((item) => (
					<div
						key={item.value}
						data-testid={testId ? `${testId}-option-${item.value}` : null}
						className={`${styles.itemWrapper} ${itemClassName}`}
						onClick={() => handleChange(item)}
					>
						<div className={styles.item}>
							<span>{item.name}</span>

							<div>{selectedOption.value === item.value ? <CheckIcon className={'text-mercury-50'} /> : ''}</div>
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

export default Dropdown;
